// PaymentReceipt.js
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import bgApp from "../assets/bg-app.png";
import logo from "../assets/logo.png";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import {
  sendWhatsAppReceipt,
  generateUploadAndSendReceipt,
} from "../utils/receiptSender";

// ========== Componente ==========
const PaymentReceipt = () => {
  const navigate = useNavigate();
  const locationState = useLocation().state || {};

  const dummyTransaction = {
    businessPhone: "8311760335",
    businessName: "Mi Negocio de Prueba",
    receiptUrl: "https://via.placeholder.com/300",
    agentName: "Sergio",
    ownerName: "Dueño Prueba",
    date: new Date().toISOString(),
    dayAmount: 20.0,
    totalDebt: 200.0,
    totalAmount: 50.0,
    tipo: "pago",
  };

  const initialTransaction = locationState.transaction || dummyTransaction;

  const [transaction, setTransaction] = useState(initialTransaction);
  const [status, setStatus] = useState(initialTransaction.receiptUrl ? "ready" : "generating"); // generating | uploading | sending | ready | sent | error
  const [alert, setAlert] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const processedRef = useRef(false);

  const db = getFirestore();
  const storage = getStorage();

  // Flujo principal: generar → subir → enviar WhatsApp
  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    // Si ya tiene receiptUrl (viene de historial), enviar directo
    if (initialTransaction.receiptUrl) {
      setStatus("sending");
      sendWhatsAppReceipt({
        db,
        transaction: initialTransaction,
        receiptUrl: initialTransaction.receiptUrl,
      })
        .then(() => {
          setStatus("sent");
          setAlert({ type: "success", message: "Recibo enviado por WhatsApp exitosamente." });
        })
        .catch((err) => {
          console.error("Error WhatsApp:", err);
          setStatus("ready");
          setAlert({ type: "error", message: `Error al enviar: ${err?.message || "intenta de nuevo."}` });
        });
      return;
    }

    // Sin receiptUrl → generar, subir, guardar en Firestore, enviar WA
    (async () => {
      try {
        await generateUploadAndSendReceipt({
          db,
          storage,
          transaction: initialTransaction,
          onStatus: setStatus,
          onReceiptUrl: (receiptUrl) =>
            setTransaction((prev) => ({ ...prev, receiptUrl })),
        });
        setStatus("sent");
        setAlert({ type: "success", message: "Recibo enviado por WhatsApp exitosamente." });
      } catch (err) {
        console.error("Error en flujo de recibo:", err);
        setStatus("error");
        setAlert({ type: "error", message: `Error: ${err?.message || "intenta reenviar manualmente."}` });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    if (!transaction.receiptUrl) return;
    setIsSending(true);
    setAlert({ type: "info", message: "Enviando por WhatsApp..." });
    try {
      await sendWhatsAppReceipt({ db, transaction, receiptUrl: transaction.receiptUrl });
      setAlert({ type: "success", message: "Recibo enviado por WhatsApp exitosamente." });
    } catch (err) {
      console.error("Error reenvío WhatsApp:", err);
      setAlert({ type: "error", message: `Error al enviar: ${err?.message || "intenta de nuevo."}` });
    } finally {
      setIsSending(false);
    }
  };

  const statusLabel = {
    generating: "Generando comprobante...",
    uploading: "Subiendo comprobante...",
    sending: "Enviando por WhatsApp...",
    ready: "Comprobante listo",
    sent: "Enviado exitosamente",
    error: "Error al procesar",
  };

  const isProcessing = ["generating", "uploading", "sending"].includes(status);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: `url(${bgApp})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>

      {/* Header */}
      <header className="sticky top-0 z-40 w-full px-4 py-3 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, rgba(112,23,48,0.95), rgba(134,30,61,0.9))", backdropFilter: "blur(10px)" }}>
        <button
          onClick={() => navigate("/cobrador")}
          className="w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-all active:scale-95"
        >
          <FaArrowLeft size={16} />
        </button>
        <img src={logo} alt="Logo" className="h-9 object-contain" />
        <div className="w-10" />
      </header>

      {/* Content */}
      <div className="flex-1 w-full px-4 pt-6 pb-24 flex flex-col items-center" style={{ background: "linear-gradient(180deg, rgba(92,3,30,0.8) 0%, rgba(60,2,20,0.9) 100%)" }}>

        {/* Title badge */}
        <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5 ${
          transaction.tipo === "adeudo"
            ? "bg-red-500/20 border border-red-400/30 text-red-200"
            : transaction.tipo === "abono"
            ? "bg-blue-500/20 border border-blue-400/30 text-blue-200"
            : "bg-emerald-500/20 border border-emerald-400/30 text-emerald-200"
        }`}>
          {transaction.tipo === "adeudo"
            ? "Notificación de Adeudo"
            : transaction.tipo === "abono"
            ? "Notificación de Abono"
            : "Recibo de Pago"}
        </span>

        {/* Receipt image o estado de generación */}
        {transaction.receiptUrl ? (
          <div className="relative overflow-hidden rounded-2xl shadow-2xl mb-6 max-w-sm w-full"
            style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
            <img
              src={transaction.receiptUrl}
              alt="Comprobante"
              className="w-full h-auto"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 mb-6 py-10 px-6 rounded-2xl max-w-sm w-full bg-white/5 border border-white/10">
            <svg className="animate-spin h-8 w-8 text-white/50" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-white/70 text-sm font-medium">{statusLabel[status]}</p>
          </div>
        )}

        {/* Status indicator */}
        {isProcessing && transaction.receiptUrl && (
          <div className="flex items-center gap-2 mb-4">
            <svg className="animate-spin h-4 w-4 text-white/50" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-white/50 text-sm">{statusLabel[status]}</p>
          </div>
        )}

        {/* WhatsApp send button */}
        <button
          onClick={handleResend}
          disabled={isSending || isProcessing || !transaction.receiptUrl}
          className={`flex items-center justify-center gap-2 w-full max-w-sm py-3.5 rounded-2xl font-bold text-sm shadow-lg transition-all ${
            isSending || isProcessing || !transaction.receiptUrl
              ? "bg-white/10 text-white/40 cursor-not-allowed"
              : "bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98]"
          }`}
        >
          <FaWhatsapp size={18} />
          {isSending || status === "sending" ? "Enviando..." : isProcessing ? statusLabel[status] : "Reenviar por WhatsApp"}
        </button>

        {/* Status alert */}
        {alert && (
          <div className={`mt-4 px-4 py-3 rounded-xl text-sm font-medium text-center max-w-sm w-full ${
            alert.type === "success"
              ? "bg-emerald-500/20 border border-emerald-400/30 text-emerald-200"
              : alert.type === "info"
              ? "bg-blue-500/20 border border-blue-400/30 text-blue-200"
              : "bg-red-500/20 border border-red-400/30 text-red-200"
          }`}>
            {alert.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReceipt;
